import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { catchError, exhaustMap, of, pipe, tap } from 'rxjs';
import { initialAuthState, UserInfo } from './auth.model';
import { AuthDataService } from '../../services/auth-data.service';
import { PosthogService } from '../../services/posthog.service';
import { CustomUserStyle } from '../../models/user-custom-config.model';
import { LOCAL_STORAGE_KEYS } from '../../constants/storage.const';
import { SupplierPortalMembership } from '../../models/user-profile.model';
import { errorMessage } from '../../shared/util/helpers/error.helper';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialAuthState),

  withComputed((store) => ({
    selectedMember: computed(() =>
      getSelectedMember(store.memberships(), store.selectedMemberId())
    ),

    selectedSupplier: computed(() => {
      const member = getSelectedMember(
        store.memberships(),
        store.selectedMemberId()
      );
      return member?.supplier ?? null;
    }),

    selectedPortal: computed(() => {
      const member = getSelectedMember(
        store.memberships(),
        store.selectedMemberId()
      );

      if (member && member.portals.length > 0) {
        const portal = member.portals.find(
          (p) => p.toLowerCase() === 'default'
        );
        return portal ?? member.portals[0] ?? '';
      }

      return '';
    }),

    isAuthenticated: computed(() => !!store.token() && !!store.user())
  })),

  withMethods(
    (
      store,
      authDataService = inject(AuthDataService),
      posthogService = inject(PosthogService)
    ) => {
      const setPosthogCompanyGroup = () => {
        const member = getSelectedMember(
          store.memberships(),
          store.selectedMemberId()
        );

        if (member?.supplier) {
          posthogService.setCompanyGroup(
            member.supplier.agdOrgId ?? null,
            member.supplier.supplierName ?? ''
          );
        }
      };

      return {
        loadMemberships: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(store, {
                isLoading: { ...store.isLoading(), memberships: true },
                error: null
              })
            ),
            exhaustMap(() =>
              authDataService.loadMemberships().pipe(
                tap((memberships) => {
                  patchState(store, {
                    memberships,
                    isLoading: { ...store.isLoading(), memberships: false }
                  });
                  setPosthogCompanyGroup();
                }),
                catchError((error: unknown) => {
                  patchState(store, {
                    error: errorMessage(error) || 'Failed to load memberships',
                    isLoading: { ...store.isLoading(), memberships: false }
                  });
                  return of(null);
                })
              )
            )
          )
        ),

        setToken: (token: string | null) => {
          patchState(store, { token });
        },

        setUser: (user: UserInfo | null) => {
          patchState(store, { user });
          if (user?.sub) {
            posthogService.identifyUser(user.sub, null, {
              email: user.email,
              name: user.name
            });
          }
        },

        setTheme: (theme: CustomUserStyle | null) => {
          patchState(store, { theme });
        },

        setTenantId: (tenantId: string | null) => {
          patchState(store, { tenantId });
        },

        setTenantLogo: (tenantLogo: string | null) => {
          patchState(store, { tenantLogo });
        },

        setSelectedMemberId: (selectedMemberId: number) => {
          patchState(store, { selectedMemberId });
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.SELECTED_PRIMARY_ORG,
            JSON.stringify(selectedMemberId)
          );
          setPosthogCompanyGroup();
        },

        clearAuthSettings: () => {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_PRIMARY_ORG);
        }
      };
    }
  )
);

function getSelectedMember(
  memberships: SupplierPortalMembership[] | null,
  selectedMemberId: number | null
): SupplierPortalMembership | null {
  if (!memberships || memberships.length === 0) {
    return null;
  }
  const selectedPrimaryOrg =
    localStorage.getItem(LOCAL_STORAGE_KEYS.SELECTED_PRIMARY_ORG) ??
    selectedMemberId?.toString() ??
    null;

  if (selectedPrimaryOrg === null) {
    return memberships[0];
  }

  return (
    memberships.find(
      (member) => member.supplier.id === Number.parseInt(selectedPrimaryOrg)
    ) ?? null
  );
}
